import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, CheckCircle2, FileText, X, Loader2, Share2 } from "lucide-react";
import api, { formatError } from "../lib/api";
import { Crest } from "../components/bits";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { NamePicker } from "../components/NamePicker";

export const QUOTES = [
  "This is not charity. It is the debt of every night we shared one roof.",
  "We ate from the same mess. We studied under the same tube light. His fight is our fight.",
  "A brother never has to ask twice.",
  "Duty doesn't need a reason. It only needs a brother.",
  "We are not helping him. We are simply doing what hostel mates do.",
  "The room changes. The bond doesn't.",
  "Same corridor, same dreams — and today, the same prayer.",
  "When one of us stands in the storm, all of us become the roof.",
];

const BG_QUOTES = [
  { q: QUOTES[0], c: "top-[6%] left-[3%] rotate-[-4deg] w-[38rem]" },
  { q: QUOTES[3], c: "top-[38%] right-[2%] rotate-[3deg] w-[30rem]" },
  { q: QUOTES[6], c: "bottom-[18%] left-[6%] rotate-[2deg] w-[34rem]" },
  { q: QUOTES[7], c: "bottom-[2%] right-[8%] rotate-[-2deg] w-[32rem]" },
];

const RotatingQuote = () => {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((x) => (x + 1) % QUOTES.length), 5000);
    return () => clearInterval(t);
  }, []);
  return (
    <blockquote key={i} className="fade-up font-display italic text-3xl sm:text-4xl lg:text-5xl leading-tight text-stone-900 min-h-[7.5rem] sm:min-h-[9rem]" data-testid="rotating-quote">
      “{QUOTES[i]}”
    </blockquote>
  );
};

const Field = ({ label, children, optional }) => (
  <div className="space-y-1.5">
    <Label className="font-typewriter text-[13px] text-stone-700">{label} {optional ? <span className="text-stone-400">(optional)</span> : <span className="text-rose-600">*</span>}</Label>
    {children}
  </div>
);

const ProofPicker = ({ file, setFile }) => {
  const ref = useRef(null);
  const preview = file && file.type.startsWith("image/") ? URL.createObjectURL(file) : null;
  return (
    <div>
      <input ref={ref} type="file" accept="image/*,application/pdf" hidden data-testid="contribution-screenshot-input"
        onChange={(e) => { setFile(e.target.files?.[0] || null); e.target.value = ""; }} />
      {!file ? (
        <button type="button" onClick={() => ref.current?.click()} data-testid="contribution-screenshot-button"
          className="w-full border-2 border-dashed border-stone-300 hover:border-amber-500 hover:bg-amber-50/50 rounded-lg py-8 flex flex-col items-center gap-2 text-stone-600 transition-colors">
          <UploadCloud className="h-7 w-7 text-amber-700" />
          <span className="font-semibold">Tap to attach payment screenshot</span>
          <span className="text-xs text-stone-500">JPG, PNG or PDF • up to 10 MB</span>
        </button>
      ) : (
        <div className="flex items-center gap-3 border border-stone-300 rounded-lg p-3 bg-stone-50" data-testid="contribution-screenshot-preview">
          {preview ? <img src={preview} alt="Payment proof" className="h-16 w-16 object-cover rounded" /> : <FileText className="h-10 w-10 text-amber-700" />}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold truncate">{file.name}</p>
            <p className="text-xs text-stone-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
          </div>
          <button type="button" onClick={() => setFile(null)} data-testid="contribution-screenshot-remove" className="p-2 rounded-full hover:bg-stone-200"><X className="h-4 w-4" /></button>
        </div>
      )}
    </div>
  );
};

const ThankYou = ({ name, onAgain }) => {
  const share = () => {
    const text = `One of us needs help. I just recorded my contribution for our hostel brother. Not charity — duty. Record yours here: ${window.location.href}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };
  return (
    <div className="text-center py-6 fade-up" data-testid="contribution-success">
      <CheckCircle2 className="h-14 w-14 text-teal-700 mx-auto" />
      <h2 className="text-3xl font-bold mt-4">Thank you, {name.split(" ")[0]}.</h2>
      <p className="text-stone-700 mt-3 font-display italic text-xl">“Your duty is recorded. Our brother will know he was never alone.”</p>
      <div className="mt-8 flex flex-col gap-3">
        <button onClick={share} data-testid="contribution-share-whatsapp" className="inline-flex items-center justify-center gap-2 bg-teal-700 hover:bg-teal-800 text-stone-50 font-semibold px-6 py-3 rounded-full"><Share2 className="h-4 w-4" />Share on WhatsApp group</button>
        <button onClick={onAgain} data-testid="contribution-submit-another" className="text-sm font-semibold text-stone-600 underline underline-offset-4">Record another contribution</button>
      </div>
    </div>
  );
};

const EMPTY = { name: "", mobile: "", email: "", amount: "", anonymous: false };

export default function Contribute() {
  const [f, setF] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const [count, setCount] = useState(0);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const loadCount = () => api.get("/contributions/public").then(({ data }) => setCount(data.count));
  useEffect(() => { loadCount(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (f.name.trim().length < 2) return toast.error("Please select your name from the list or add a new one");
    setSending(true);
    try {
      const form = new FormData();
      Object.entries(f).forEach(([k, v]) => form.append(k, v));
      if (file) form.append("screenshot", file);
      await api.post("/contributions", form);
      setDone(f.name);
      setF(EMPTY);
      setFile(null);
      loadCount();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden">
      <div aria-hidden className="pointer-events-none absolute inset-0 hidden md:block">
        {BG_QUOTES.map((b) => (
          <p key={b.q} className={`absolute font-display italic text-5xl leading-tight text-stone-900/[0.06] ${b.c}`}>“{b.q}”</p>
        ))}
      </div>

      <header className="relative z-10 max-w-6xl mx-auto px-5 h-20 flex items-center justify-between" style={{ marginTop: "env(safe-area-inset-top)" }}>
        <div className="flex items-center gap-2.5" data-testid="contribute-brand">
          <Crest size="h-10 w-10" />
          <span className="leading-none">
            <span className="font-display font-bold text-lg text-stone-900 block">LVB Hostel</span>
            <span className="font-typewriter text-[10px] uppercase tracking-widest text-amber-800">Andheri • Est. 1904</span>
          </span>
        </div>
      </header>

      <main className="relative z-10 max-w-6xl mx-auto px-5 pb-20 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
        <section className="lg:col-span-6 lg:sticky lg:top-8 pt-2">
          <span className="stamp inline-block text-rose-600 text-xs px-3 py-1 -rotate-2">One of us needs help</span>
          <div className="mt-6"><RotatingQuote /></div>
          <p className="mt-6 text-stone-700 text-base sm:text-lg leading-relaxed max-w-lg">
            One of us — same hostel, same corridors, same late-night chai — is going through a medical emergency, and his hostel family has decided to stand by him.
            If you have sent your contribution, please record it here so we keep a clean, transparent account.
          </p>
          <div className="mt-8 flex items-end gap-6">
            <figure className="polaroid relative w-44 sm:w-52 -rotate-3 shrink-0">
              <img src="/brand/hostel-family.jpg" alt="Hostel brothers" className="vintage-photo w-full aspect-square object-cover object-top" />
              <figcaption className="caption">One roof. One family.</figcaption>
            </figure>
            <div data-testid="contribution-count">
              <p className="font-display text-5xl font-bold text-stone-900">{count}</p>
              <p className="eyebrow mt-1">{count === 1 ? "brother has" : "brothers have"} done their duty</p>
            </div>
          </div>
        </section>

        <section className="lg:col-span-6">
          <div className="relative bg-white border border-stone-300 rounded-lg shadow-[0_18px_40px_-18px_rgba(40,33,26,0.45)] overflow-hidden" data-testid="contribution-form-card">
            <div className="h-2 bg-amber-700" />
            <div className="p-6 sm:p-8">
              {done ? <ThankYou name={done} onAgain={() => setDone(null)} /> : (
                <>
                  <h1 className="text-3xl sm:text-4xl font-bold text-stone-900">Record your contribution</h1>
                  <p className="text-sm text-stone-600 mt-2">Takes 1 minute. Your details are visible only to the organising admins — you can also hide your name from the beneficiary at the end.</p>
                  <form onSubmit={submit} className="mt-7 space-y-5" data-testid="contribution-form">
                    <Field label="Full name"><NamePicker value={f.name} onChange={(name) => setF((p) => ({ ...p, name }))} reloadKey={count} /></Field>
                    <Field label="Mobile number"><Input required type="tel" inputMode="tel" value={f.mobile} onChange={set("mobile")} placeholder="98XXXXXXXX" className="h-12 text-base" data-testid="contribution-mobile-input" /></Field>
                    <Field label="Email ID"><Input required type="email" inputMode="email" value={f.email} onChange={set("email")} placeholder="you@example.com" className="h-12 text-base" data-testid="contribution-email-input" /></Field>
                    <Field label="Contribution amount (₹)">
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 font-display text-lg text-stone-500">₹</span>
                        <Input required type="number" inputMode="decimal" min="1" step="any" value={f.amount} onChange={set("amount")} className="h-12 text-base pl-8" data-testid="contribution-amount-input" />
                      </div>
                    </Field>
                    <Field label="Payment screenshot" optional><ProofPicker file={file} setFile={setFile} /></Field>
                    <label className={`flex items-start gap-3 rounded-lg border p-4 cursor-pointer transition-colors ${f.anonymous ? "border-amber-400 bg-amber-50" : "border-stone-300 bg-stone-50"}`} data-testid="contribution-anonymous-option">
                      <Checkbox checked={f.anonymous} onCheckedChange={(v) => setF({ ...f, anonymous: !!v })} data-testid="contribution-anonymous-checkbox" className="mt-0.5" />
                      <span className="text-sm text-stone-700">
                        <span className="font-semibold text-stone-900 block">Keep my name hidden from the beneficiary</span>
                        Only the organising admins will see your details. Your name will appear as "Anonymous" in every report shared with the family.
                      </span>
                    </label>
                    <button type="submit" disabled={sending} data-testid="contribution-submit-button"
                      className="w-full h-13 py-3.5 rounded-full bg-stone-900 hover:bg-stone-800 text-stone-50 font-semibold text-base inline-flex items-center justify-center gap-2 disabled:opacity-60">
                      {sending ? <><Loader2 className="h-4 w-4 animate-spin" />Submitting...</> : "Submit — I stand with my brother"}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
          <p className="text-center text-xs text-stone-500 font-typewriter mt-6">LVB Hostel family • Andheri, Mumbai</p>
        </section>
      </main>
    </div>
  );
}
